import { useAuth } from "@clerk/clerk-react";
import { LoadingButton } from "@mui/lab";
import { Box, Chip, FormHelperText, Typography } from "@mui/material";
import { grey } from "@mui/material/colors";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { CheckoutState, SubscriptionContext } from "contexts/Subscription";
import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AddOnItemName,
  SubscriptionPortalFlow,
  SubscriptionType,
} from "schemas/dashboard";
import { routes } from "schemas/routes";

import SeeExample from "components/StripeCheckout/components/SeeExample";
import CancelDialog from "features/Home/UpgradeDialog/Plan/CancelDialog";
import SubscriptionStatusText from "features/Home/UpgradeDialog/Plan/SubscriptionStatusText";
import { getStatusChipLabel } from "features/Home/UpgradeDialog/Plan/helpers";
import { fetcherAuth } from "utils/api";
import { SubscriptionColor } from "utils/color";
import { accessToVaFeature } from "utils/influencer";
import { getParams, getSource, trackEvent } from "utils/tracking";

import ManageAddOnDialog from "./ManageAddOnDialog";
import styles from "./styles";

const AddOnPartnershipData = () => {
  const source = getSource();
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const { currentOrg } = useContext(OrganizationUserContext);
  const { setErrorAlert, setAlert } = useContext(AlertContext);
  const {
    subscription,
    setSubscription,
    plans,
    addOns,
    isVaPlan,
    setOpenCheckoutDialog,
    upgradeDialogSource,
  } = useContext(SubscriptionContext);

  const [portalLoading, setPortalLoading] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [openManageDialog, setOpenManageDialog] = useState(false);
  const [openCancelDialog, setOpenCancelDialog] = useState(false);

  const hasAccessToVa = accessToVaFeature(currentOrg, subscription, isVaPlan);

  const backgroundColor = subscription?.partnershipData?.status
    ? SubscriptionColor[subscription?.partnershipData?.status]
    : "";
  const label = getStatusChipLabel(subscription?.partnershipData?.status);

  const getButtonName = (): {
    title: string;
    variant: "text" | "outlined" | "contained";
    action: () => void;
  } => {
    if (subscription && subscription?.partnershipData) {
      if (subscription?.partnershipData?.cancelAt) {
        return {
          title: "Renew",
          variant: "contained",
          action: handleRenew,
        };
      } else if (
        subscription?.partnershipData?.status === SubscriptionType.CANCELED
      ) {
        return {
          title: "Resubscribe",
          variant: "contained",
          action: handleCheckout,
        };
      } else if (
        subscription?.partnershipData?.status === SubscriptionType.INCOMPLETE
      ) {
        return {
          title: "Retry",
          variant: "contained",
          action: handleCheckout,
        };
      } else {
        return {
          title: "Manage Add-on",
          variant: "outlined",
          action: () => setOpenManageDialog(true),
        };
      }
    } else {
      return {
        title: "Purchase",
        variant: "contained",
        action: handleCheckout,
      };
    }
  };

  const handleCheckout = async () => {
    if (!plans || !addOns) return;
    const state: CheckoutState = {
      plan: AddOnItemName.partnership_data,
      source,
      pageSource: window.location.pathname,
      coupon: getParams("coupon"),
    };
    if (upgradeDialogSource) {
      setOpenCheckoutDialog(state);
    } else {
      navigate(`/${routes.subscriptions}`, {
        state,
      });
    }
  };

  const handleManage = async (flow?: SubscriptionPortalFlow) => {
    try {
      setPortalLoading(true);
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/payments-add-ons/${subscription?.partnershipData?.id}/manage`,
        "POST",
        {},
        {
          flow,
        },
      );
      window.open(res.session.url, "_self");
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setPortalLoading(false);
    }
  };

  const handleRenew = async () => {
    setButtonLoading(true);
    try {
      setPortalLoading(true);
      const { addOnItem } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/payments-add-ons/${subscription?.partnershipData?.id}/renew`,
        "POST",
      );
      setAlert("Successfully renewed subscription", "success");
      if (addOnItem) {
        setSubscription((prev) =>
          prev
            ? {
                ...prev,
                partnershipData: addOnItem,
              }
            : prev,
        );
      }
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setButtonLoading(false);
      setPortalLoading(false);
    }
  };

  const openCancel = () => {
    trackEvent(
      "Manage Subscription Dialog Cancel Subscription Button Pressed",
      {
        plan: AddOnItemName.partnership_data,
      },
    );
    setOpenManageDialog(false);
    setOpenCancelDialog(true);
  };

  const closeCancelDialog = () => {
    trackEvent("Cancel Subscription Dialog Closed", {
      plan: AddOnItemName.partnership_data,
    });
    setOpenCancelDialog(false);
  };

  return (
    <Box sx={{ pr: { xs: 0, lg: 10 } }}>
      <Typography sx={{ mb: 1, fontSize: 16 }} variant="h6" component="div">
        Partnership Data{" "}
        {label && !hasAccessToVa && (
          <Chip
            size="small"
            sx={[styles.chip, { backgroundColor }]}
            label={label}
          />
        )}{" "}
        <br />${addOns?.[AddOnItemName.partnership_data]?.price.toFixed(2)}
        /month
        <SeeExample theme="dark" />
      </Typography>
      {hasAccessToVa ? (
        <FormHelperText sx={{ color: "info.main", mb: 2 }}>
          <strong>
            This feature is already included in your Virtual Assistant plan
          </strong>
        </FormHelperText>
      ) : (
        <FormHelperText sx={{ color: grey[500], mb: 2 }}>
          Users can purchase on Free plan or Pro plan. Partnerships data is
          included in the <strong>Virtual Assistant</strong> plan.
        </FormHelperText>
      )}
      <Box>
        Access insider data on brand partnerships - see if they pay, gifted
        products, when they last collaborated, where they operate, and if their
        contact is verified or responsive.
      </Box>
      {!hasAccessToVa && (
        <>
          <SubscriptionStatusText
            status={
              subscription?.partnershipData?.status || SubscriptionType.TRIALING
            }
            cancelAt={subscription?.partnershipData?.cancelAt}
          />
          <LoadingButton
            onClick={getButtonName()?.action}
            disableElevation
            variant={getButtonName()?.variant}
            loading={buttonLoading}
          >
            {getButtonName()?.title}
          </LoadingButton>
        </>
      )}
      <ManageAddOnDialog
        open={openManageDialog}
        handleManagePlan={handleManage}
        loading={portalLoading}
        onClose={() => setOpenManageDialog(false)}
        openCancel={openCancel}
      />

      <CancelDialog
        loading={portalLoading}
        open={openCancelDialog}
        onClose={closeCancelDialog}
        warningText="You will lose all access to exclusive partnerships and contacts data."
        handleManagePlan={handleManage}
        plan={AddOnItemName.partnership_data}
      />
    </Box>
  );
};

export default AddOnPartnershipData;
